export default class ReadingWindow {
  constructor() {
    this.active = false;
    this.overlay = null;
    this.hole = null;
    this.dragStart = null;
    this.resizeStart = null;
    this.cleanup = null;
    this.pointerMoveHandler = this.handlePointerMove.bind(this);
    this.pointerUpHandler = this.handlePointerUp.bind(this);
    this.outsidePointerHandler = this.handleOutsidePointer.bind(this);
  }

  enable() {
    if (this.active) return;
    this.active = true;

    const overlay = document.createElement('div');
    overlay.className = 'a11y-reading-window';
    const hole = document.createElement('div');
    hole.className = 'a11y-reading-window__hole';
    overlay.appendChild(hole);

    const handles = ['nw', 'ne', 'sw', 'se'];
    handles.forEach((handle) => {
      const handleEl = document.createElement('div');
      handleEl.className = `a11y-reading-window__handle a11y-reading-window__handle--${handle}`;
      handleEl.dataset.handle = handle;
      hole.appendChild(handleEl);
      handleEl.addEventListener('pointerdown', (event) => this.handleResizeStart(event));
    });

    document.body.appendChild(overlay);

    this.overlay = overlay;
    this.hole = hole;

    const rect = hole.getBoundingClientRect();
    this.setPosition((window.innerWidth - rect.width) / 2, (window.innerHeight - rect.height) / 3);

    hole.addEventListener('pointerdown', (event) => this.handleDragStart(event));

    hole.addEventListener('wheel', (e) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -20 : 20;
      const width = Math.min(Math.max(hole.offsetWidth + delta, 200), window.innerWidth - 40);
      const height = Math.min(Math.max(hole.offsetHeight + delta, 140), window.innerHeight - 40);
      hole.style.width = `${width}px`;
      hole.style.height = `${height}px`;
    }, { passive: false });

    document.addEventListener('pointermove', this.pointerMoveHandler);
    document.addEventListener('pointerup', this.pointerUpHandler);
    document.addEventListener('pointercancel', this.pointerUpHandler);
    document.addEventListener('pointerdown', this.outsidePointerHandler, true);

    this.cleanup = () => {
      document.removeEventListener('pointermove', this.pointerMoveHandler);
      document.removeEventListener('pointerup', this.pointerUpHandler);
      document.removeEventListener('pointercancel', this.pointerUpHandler);
      document.removeEventListener('pointerdown', this.outsidePointerHandler, true);
    };
  }

  handleDragStart(event) {
    if (!this.hole) return;
    if (event.target.closest('.a11y-reading-window__handle')) return;
    event.preventDefault();

    const rect = this.hole.getBoundingClientRect();
    this.dragStart = {
      x: event.clientX,
      y: event.clientY,
      left: rect.left,
      top: rect.top,
    };
  }

  handleResizeStart(event) {
    if (!this.hole) return;
    event.preventDefault();
    event.stopPropagation();

    const rect = this.hole.getBoundingClientRect();
    this.resizeStart = {
      handle: event.currentTarget.dataset.handle,
      x: event.clientX,
      y: event.clientY,
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height,
    };
  }

  handlePointerMove(event) {
    if (this.dragStart) {
      event.preventDefault();
      const left = this.dragStart.left + (event.clientX - this.dragStart.x);
      const top = this.dragStart.top + (event.clientY - this.dragStart.y);
      this.setPosition(left, top);
      return;
    }

    if (!this.resizeStart || !this.hole) return;

    event.preventDefault();

    const minWidth = 200;
    const minHeight = 140;
    const maxWidth = window.innerWidth - 20;
    const maxHeight = window.innerHeight - 20;
    const dx = event.clientX - this.resizeStart.x;
    const dy = event.clientY - this.resizeStart.y;
    const handle = this.resizeStart.handle;

    let width = this.resizeStart.width;
    let height = this.resizeStart.height;
    let left = this.resizeStart.left;
    let top = this.resizeStart.top;

    if (handle.includes('e')) width += dx;
    if (handle.includes('w')) width -= dx;
    if (handle.includes('s')) height += dy;
    if (handle.includes('n')) height -= dy;

    width = Math.min(Math.max(width, minWidth), maxWidth);
    height = Math.min(Math.max(height, minHeight), maxHeight);

    if (handle.includes('w')) left = this.resizeStart.left + (this.resizeStart.width - width);
    if (handle.includes('n')) top = this.resizeStart.top + (this.resizeStart.height - height);

    this.hole.style.width = `${width}px`;
    this.hole.style.height = `${height}px`;
    this.setPosition(left, top);
  }

  handlePointerUp() {
    this.dragStart = null;
    this.resizeStart = null;
  }

  handleOutsidePointer(event) {
    if (!this.active || !this.hole) return;
    if (event.target.closest('.a11y-sidebar')) return;
    if (event.target.closest('.a11y-reading-window__hole')) return;

    const rect = this.hole.getBoundingClientRect();
    this.setPosition(event.clientX - rect.width / 2, event.clientY - rect.height / 2);
  }

  disable() {
    if (!this.active) return;
    if (this.cleanup) this.cleanup();
    this.overlay?.remove();
    this.overlay = null;
    this.hole = null;
    this.active = false;
  }

  setPosition(x, y) {
    if (!this.hole) return;
    const margin = 10;
    const maxX = window.innerWidth - this.hole.offsetWidth - margin;
    const maxY = window.innerHeight - this.hole.offsetHeight - margin;
    this.hole.style.left = `${Math.max(margin, Math.min(maxX, x))}px`;
    this.hole.style.top = `${Math.max(margin, Math.min(maxY, y))}px`;
  }
}
